import { Languages } from '@account/enums/languages.enum';
import { formatDate } from '@angular/common';
import { ApprovalFlowStatus } from '@app/enums';
import { AuthService } from '@app/services';
import { Translatable, TranslatableKey } from '@app/types/translatable.type';
import { ChangeRequestStepStatus } from '@change-requests/enums/change-request-step-status.enum';
import { Employee } from '@models/employee/employee.model';
export default class ChangeRequestFlowStep {
    protected titleMap = new Map<ChangeRequestStepStatus, string>([
        [ChangeRequestStepStatus.Approved, 'change-requests.approvalFlowSteps.requestApproved'],
        [ChangeRequestStepStatus.Denied, 'change-requests.approvalFlowSteps.requestDenied'],
        [ChangeRequestStepStatus.ForcedApproved, 'change-requests.approvalFlowSteps.forceApproved'],
        [ChangeRequestStepStatus.ForcedDenied, 'change-requests.approvalFlowSteps.forceDenied'],
        [ChangeRequestStepStatus.Submitted, 'change-requests.approvalFlowSteps.requestSubmitted'],
        [ChangeRequestStepStatus.Pending, 'change-requests.approvalFlowSteps.awaitingApproval'],
        [ChangeRequestStepStatus.Cancelled, 'change-requests.approvalFlowSteps.requestCancelled'],
        [ChangeRequestStepStatus.EffectiveAt, 'change-requests.approvalFlowSteps.effectiveAt'],
        [ChangeRequestStepStatus.Completed, 'change-requests.approvalFlowSteps.effectiveAt'],
    ]);

    protected negativeStepStatuses = [
        ChangeRequestStepStatus.Denied,
        ChangeRequestStepStatus.ForcedDenied,
        ChangeRequestStepStatus.Cancelled,
    ];

    protected positiveStepStatuses = [
        ChangeRequestStepStatus.Approved,
        ChangeRequestStepStatus.ForcedApproved,
        ChangeRequestStepStatus.Submitted,
        ChangeRequestStepStatus.Completed,
    ];

    constructor(
        public actor: string | Employee,
        public status: ChangeRequestStepStatus,
        private auth: AuthService,
        public actionedOn?: Date | string,
        public closingComment?: string
    ) {}

    get title(): TranslatableKey {
        return this.titleMap.get(this.status);
    }

    get description(): Translatable {
        if (!this.actor) {
            return '';
        }

        const employee = this.auth.user;

        const isPending = this.status === ChangeRequestStepStatus.Pending;

        if (typeof this.actor === 'string') {
            if (isPending) {
                return {
                    key: 'change-requests.approvalFlowDescription.actorStringFrom',
                    params: {
                        actor: this.actor,
                    },
                };
            } else {
                return {
                    key: 'change-requests.approvalFlowDescription.actorStringBy',
                    params: {
                        actor: this.actor,
                    },
                };
            }
        }

        if (this.status === ChangeRequestStepStatus.EffectiveAt) {
            return {
                key: 'change-requests.approvalFlowDescription.statusEffectiveAt',
                params: {
                    date: formatDate(this.actionedOn, 'longDate', employee.language ?? Languages.ENGLISH),
                    firstName: this.actor.firstName,
                },
            };
        }

        if (this.status === ChangeRequestStepStatus.Completed) {
            return {
                key: 'change-requests.approvalFlowDescription.statusCompleted',
                params: {
                    date: formatDate(this.actionedOn, 'longDate', employee.language ?? Languages.ENGLISH),
                    firstName: this.actor.firstName,
                },
            };
        }

        if (this.actionedOn === null) {
            if (isPending) {
                return {
                    key: 'change-requests.approvalFlowDescription.actionNullFrom',
                    params: {
                        actorFullname: this.actor.fullName,
                    },
                };
            } else {
                return {
                    key: 'change-requests.approvalFlowDescription.actionNullBy',
                    params: {
                        actorFullname: this.actor.fullName,
                    },
                };
            }
        }

        if (this.shouldShowClosingComment()) {
            if (isPending) {
                return {
                    key: 'change-requests.approvalFlowDescription.shouldShowClosingCommentFrom',
                    params: {
                        fullName: this.actor.fullName,
                        date: formatDate(this.actionedOn, 'longDate', employee.language ?? Languages.ENGLISH),
                        comment: this.closingComment,
                    },
                };
            } else {
                return {
                    key: 'change-requests.approvalFlowDescription.shouldShowClosingCommentBy',
                    params: {
                        fullName: this.actor.fullName,
                        date: formatDate(this.actionedOn, 'longDate', employee.language ?? Languages.ENGLISH),
                        comment: this.closingComment,
                    },
                };
            }
        }

        if (isPending) {
            return {
                key: 'change-requests.approvalFlowDescription.defaultFrom',
                params: {
                    fullName: this.actor.fullName,
                    date: formatDate(this.actionedOn, 'longDate', employee.language ?? Languages.ENGLISH),
                },
            };
        } else {
            return {
                key: 'change-requests.approvalFlowDescription.defaultBy',
                params: {
                    fullName: this.actor.fullName,
                    date: formatDate(this.actionedOn, 'longDate', employee.language ?? Languages.ENGLISH),
                },
            };
        }
    }

    get componentStatus(): ApprovalFlowStatus | null {
        if (!this.actor && this.status === ChangeRequestStepStatus.Submitted) {
            return null;
        }

        if (this.positiveStepStatuses.includes(this.status)) {
            return ApprovalFlowStatus.PositiveStatus;
        }

        if (this.negativeStepStatuses.includes(this.status)) {
            return ApprovalFlowStatus.NegativeStatus;
        }

        return null;
    }

    private shouldShowClosingComment(): boolean {
        return [
            ChangeRequestStepStatus.Denied,
            ChangeRequestStepStatus.ForcedApproved,
            ChangeRequestStepStatus.ForcedDenied,
        ].includes(this.status);
    }
}
